/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { useStyletron } from "baseui";
import { PLACEMENT } from "baseui/popover";
import { Button, KIND, SIZE, SHAPE } from "baseui/button";
import { StatefulTooltip } from "baseui/tooltip";
import {
  FaChevronDown,
  FaDownload,
  FaEllipsisH,
  FaFolderOpen,
  FaPlus,
  FaRegSave,
  FaRegTrashAlt,
} from "react-icons/fa";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";
import { DropdownMenu } from "./DropdownMenu";

interface TransformEngineHeaderProps {
  /** Title editor, rendered on the left. */
  title: React.ReactNode;
  /** Optional List/Canvas view toggle, centred in the bar. */
  viewToggle?: React.ReactNode;
  onNewMapping: () => void;
  /** Populate Canvas → Upload files (populate modal). */
  onUploadFiles: () => void;
  /** Populate Canvas → Open saved mapping (load mapping modal). */
  onOpenMapping: () => void;
  /** Populate Canvas → Upload mapping (OS file picker for JSON). */
  onUploadMapping: () => void;

  onDownloadConfig: () => void;
  onDownloadImage: () => void;
  canDownloadImage: boolean;
  onDownloadOutputs: () => void;
  canDownloadOutputs: boolean;

  onSave: () => void;
  onSaveAs: () => void;
  onSaveAndClose: () => void;

  /** Remove every node and edge (after confirmation). */
  onClearCanvas: () => void;
  canClearCanvas: boolean;
}

export const TRANSFORM_ENGINE_HEADER_HEIGHT = 48;

/** Text size around the List/Canvas toggle. Must match the List view header. */
export const VIEW_TOGGLE_FONT_SIZE = "14px";

/** Shared Dexter tooltip under a header button. */
function HeaderTooltip({
  content,
  children,
}: {
  content: string;
  children: React.ReactElement;
}): JSX.Element {
  const { currentTheme } = useDexterTheme();
  return (
    <StatefulTooltip
      placement={PLACEMENT.bottom}
      content={content}
      overrides={getTooltipOverrides(currentTheme)}
    >
      {/* Wrapper element so the tooltip can attach to the button. */}
      <span style={{ display: "inline-flex" }}>{children}</span>
    </StatefulTooltip>
  );
}

/**
 * Mapping-level actions: title on the left; New / Open / Download / Save / More
 * on the right.
 */
export function TransformEngineHeader(
  props: TransformEngineHeaderProps
): JSX.Element {
  const [css] = useStyletron();
  const { currentTheme } = useDexterTheme();
  const c = currentTheme.colors;

  type HeaderButtonVariant =
    | "ghost"
    | "primaryMain"
    | "primaryChevron"
    | "iconOnly";

  const hover = "rgba(127,127,127,0.16)";
  const pressed = "rgba(127,127,127,0.22)";

  /**
   * Base Web Button overrides so every header button shares one look:
   * 32px tall, 6px corners, theme text colour. "primary*" are the inverted
   * Save split button; "ghost"/"iconOnly" are transparent until hovered.
   */
  const headerButtonOverrides = (
    variant: HeaderButtonVariant
  ): React.ComponentProps<typeof Button>["overrides"] => {
    const isPrimary = variant === "primaryMain" || variant === "primaryChevron";
    const radius = "6px";
    return {
      BaseButton: {
        style: ({ $disabled, $isFocusVisible }) => ({
          height: "32px",
          minWidth: variant === "iconOnly" ? "32px" : undefined,
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft:
            variant === "iconOnly" || variant === "primaryChevron"
              ? "9px"
              : "12px",
          paddingRight:
            variant === "iconOnly" || variant === "primaryChevron"
              ? "9px"
              : "12px",
          borderTopLeftRadius: variant === "primaryChevron" ? 0 : radius,
          borderBottomLeftRadius: variant === "primaryChevron" ? 0 : radius,
          borderTopRightRadius: variant === "primaryMain" ? 0 : radius,
          borderBottomRightRadius: variant === "primaryMain" ? 0 : radius,
          ...(variant === "primaryChevron"
            ? {
                borderLeftWidth: "1px",
                borderLeftStyle: "solid",
                borderLeftColor: c.reactFlowBackground,
              }
            : {}),
          fontFamily: currentTheme.fonts?.primary ?? "inherit",
          fontSize: "15px",
          fontWeight: 500,
          lineHeight: "20px",
          whiteSpace: "nowrap",
          backgroundColor: isPrimary ? c.text : "transparent",
          color: isPrimary ? c.reactFlowBackground : c.text,
          opacity: $disabled ? 0.5 : 1,
          cursor: $disabled ? "default" : "pointer",
          boxShadow: $isFocusVisible ? `0 0 0 2px ${c.text}` : "none",
          ":hover": isPrimary
            ? { backgroundColor: c.text, opacity: 0.88 }
            : { backgroundColor: $disabled ? "transparent" : hover },
          ":active": isPrimary
            ? { backgroundColor: c.text, opacity: 0.8 }
            : { backgroundColor: $disabled ? "transparent" : pressed },
          // Highlight a menu trigger while its dropdown is open (the popover
          // sets aria-expanded on the trigger).
          ':is([aria-expanded="true"])': isPrimary
            ? { opacity: 0.8 }
            : { backgroundColor: pressed },
          // Keep our colours instead of Base Web's grey disabled style.
          ":disabled": {
            backgroundColor: isPrimary ? c.text : "transparent",
            color: isPrimary ? c.reactFlowBackground : c.text,
          },
        }),
      },
      StartEnhancer: { style: { marginRight: "8px" } },
      EndEnhancer: { style: { marginLeft: "8px" } },
    };
  };

  return (
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "16px",
        height: `${TRANSFORM_ENGINE_HEADER_HEIGHT}px`,
        minHeight: `${TRANSFORM_ENGINE_HEADER_HEIGHT}px`,
        padding: "0 16px",
        backgroundColor: c.backgroundTertiary,
        borderTop: `1px solid ${c.controlBarBorderColor}`,
        borderBottom: `1px solid ${c.controlBarBorderColor}`,
        boxSizing: "border-box",
      })}
    >
      {/*
        Three columns: left and right both flex from a zero basis so they get
        equal widths, which keeps the middle (view toggle) centred on the bar.
        The right column can't shrink below its buttons, so on narrow screens
        the toggle shifts left rather than overlapping them.
      */}
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          flex: "1 1 0",
          minWidth: 0,
        })}
      >
        {props.title}
      </div>

      {props.viewToggle && (
        <div
          data-testid="header-view-toggle"
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            // The same toggle is also shown in the List view's header. It sizes
            // itself from the text size around it, so pin that here to match
            // the List view; otherwise it inherits the page default and the
            // pills shrink when switching to Canvas.
            fontSize: VIEW_TOGGLE_FONT_SIZE,
          })}
        >
          {props.viewToggle}
        </div>
      )}

      <div
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "6px",
          flex: "1 1 0",
        })}
      >
        <HeaderTooltip content="Clear canvas and start a new mapping">
          <Button
            id="clear-mapping-button"
            kind={KIND.tertiary}
            size={SIZE.compact}
            startEnhancer={<FaPlus size={13} />}
            onClick={props.onNewMapping}
            overrides={headerButtonOverrides("ghost")}
          >
            New Mapping
          </Button>
        </HeaderTooltip>

        <DropdownMenu
          placement={PLACEMENT.bottomLeft}
          minWidth={230}
          items={[
            {
              id: "upload-mapping-files-button",
              label: "Upload files",
              tooltip: "Upload files to create canvas input nodes",
              onSelect: props.onUploadFiles,
            },
            {
              id: "load-nondemo-mapping-button",
              label: "Open saved mapping",
              tooltip: "Populate canvas with a saved mapping",
              onSelect: props.onOpenMapping,
            },
            {
              id: "upload-mapping-json-button",
              label: "Upload mapping",
              tooltip: "Upload mapping config",
              hint: "json",
              onSelect: props.onUploadMapping,
            },
          ]}
        >
          <Button
            id="open-mapping-menu-button"
            aria-haspopup="menu"
            kind={KIND.tertiary}
            size={SIZE.compact}
            startEnhancer={<FaFolderOpen size={16} />}
            endEnhancer={<FaChevronDown size={11} />}
            overrides={headerButtonOverrides("ghost")}
          >
            Populate Canvas
          </Button>
        </DropdownMenu>

        <DropdownMenu
          placement={PLACEMENT.bottomRight}
          minWidth={210}
          items={[
            {
              id: "download-mapping-json-button",
              label: "Mapping config",
              tooltip: "Save mapping as a JSON file",
              hint: "json",
              onSelect: props.onDownloadConfig,
            },
            {
              id: "download-canvas-image-button",
              label: "Image of canvas",
              tooltip: "Save mapping as a PNG image",
              hint: "png",
              disabled: !props.canDownloadImage,
              disabledReason: "Add a node to the canvas first",
              onSelect: props.onDownloadImage,
            },
            {
              id: "download-outputs-zip-button",
              label: "All outputs",
              hint: "zip",
              disabled: !props.canDownloadOutputs,
              disabledReason: "No outputs ready for download",
              onSelect: props.onDownloadOutputs,
            },
          ]}
        >
          <Button
            id="download-menu-button"
            aria-haspopup="menu"
            kind={KIND.tertiary}
            size={SIZE.compact}
            startEnhancer={<FaDownload size={14} />}
            endEnhancer={<FaChevronDown size={11} />}
            overrides={headerButtonOverrides("ghost")}
          >
            Download
          </Button>
        </DropdownMenu>

        <div className={css({ display: "inline-flex", marginLeft: "6px" })}>
          <HeaderTooltip content="Save mapping">
            <Button
              id="save-mapping-button"
              kind={KIND.primary}
              size={SIZE.compact}
              startEnhancer={<FaRegSave size={15} />}
              onClick={props.onSave}
              overrides={headerButtonOverrides("primaryMain")}
            >
              Save
            </Button>
          </HeaderTooltip>
          <DropdownMenu
            placement={PLACEMENT.bottomRight}
            minWidth={170}
            items={[
              {
                id: "save-mapping-menu-item",
                label: "Save",
                tooltip: "Save mapping",
                onSelect: props.onSave,
              },
              {
                id: "save-mapping-as-button",
                label: "Save as…",
                tooltip: "Save mapping with a new name",
                onSelect: props.onSaveAs,
              },
              {
                id: "save-and-close-mapping-button",
                label: "Save and close",
                tooltip: "Save mapping and open saved mappings list",
                onSelect: props.onSaveAndClose,
              },
            ]}
          >
            <Button
              id="save-options-menu-button"
              aria-label="More save options"
              aria-haspopup="menu"
              kind={KIND.primary}
              size={SIZE.compact}
              overrides={headerButtonOverrides("primaryChevron")}
            >
              <FaChevronDown size={11} />
            </Button>
          </DropdownMenu>
        </div>

        {/* Clear canvas */}
        <StatefulTooltip
          placement={PLACEMENT.bottom}
          content={props.canClearCanvas ? "Clear canvas" : "Canvas is empty"}
          overrides={getTooltipOverrides(currentTheme)}
        >
          {/* Wrapper so the tooltip still shows while the button is disabled. */}
          <span style={{ display: "inline-flex" }}>
            <Button
              id="clear-canvas-button"
              data-testid="clear-canvas-button"
              aria-label="Clear canvas"
              kind={KIND.tertiary}
              size={SIZE.compact}
              shape={SHAPE.square}
              disabled={!props.canClearCanvas}
              onClick={props.onClearCanvas}
              overrides={{
                BaseButton: {
                  style: ({ $disabled }) => ({
                    width: "32px",
                    height: "32px",
                    borderTopLeftRadius: "6px",
                    borderTopRightRadius: "6px",
                    borderBottomLeftRadius: "6px",
                    borderBottomRightRadius: "6px",
                    backgroundColor: "transparent",
                    color: c.text,
                    opacity: $disabled ? 0.45 : 1,
                    ":hover": {
                      backgroundColor: $disabled
                        ? "transparent"
                        : "rgba(127,127,127,0.16)",
                      color: $disabled ? c.text : c.negative,
                    },
                    ":disabled": {
                      backgroundColor: "transparent",
                      color: c.text,
                    },
                  }),
                },
              }}
            >
              <FaRegTrashAlt size={15} />
            </Button>
          </span>
        </StatefulTooltip>

        <Button
          id="more-actions-button"
          aria-label="More actions"
          disabled
          kind={KIND.tertiary}
          size={SIZE.compact}
          overrides={headerButtonOverrides("iconOnly")}
        >
          <FaEllipsisH size={15} />
        </Button>
      </div>
    </div>
  );
}
